/**
 * Indicatori del cruscotto.
 *
 * I quattro KPI del wireframe §6 sono **derivati** dall'alberatura e
 * dall'elenco delle pubblicazioni, non richiesti a un endpoint di riepilogo che
 * CONVENTIONS §5 non prevede. Il vantaggio non e' solo di contratto: i numeri
 * mostrati sono gli stessi che l'operatore ritrova cliccando, perche' vengono
 * dalle stesse liste.
 */

import type { Nodo, Pubblicazione, RegolaObbligo } from "../api/types";
import { calcolaScadenze, nodiFoglia, scadenzeImminenti, GIORNI_PREAVVISO } from "./scadenze";

/** Un indicatore del cruscotto, pronto per essere reso come scheda. */
export interface Indicatore {
  chiave: string;
  etichetta: string;
  valore: number;
  /** Riga di contesto sotto il numero. */
  nota: string;
  /** Tono cromatico: `attenzione` e `critico` accendono la scheda. */
  tono: "neutro" | "positivo" | "attenzione" | "critico";
  /** Percorso a cui porta la scheda. */
  collegamento: string;
}

/** Conta i documenti con validazione fallita fra le pubblicazioni date. */
export function documentiConValidazioneFallita(
  pubblicazioni: readonly Pubblicazione[],
): { pubblicazione: Pubblicazione; documento: NonNullable<Pubblicazione["documenti"]>[number] }[] {
  const esiti: {
    pubblicazione: Pubblicazione;
    documento: NonNullable<Pubblicazione["documenti"]>[number];
  }[] = [];
  for (const pubblicazione of pubblicazioni) {
    for (const documento of pubblicazione.documenti ?? []) {
      if (documento.esiti_validazione?.stato === "fallita") {
        esiti.push({ pubblicazione, documento });
      }
    }
  }
  return esiti;
}

/**
 * Nodi applicabili, di ultimo livello, senza alcuna pubblicazione.
 *
 * `nodiAmmessi` restringe al perimetro di chi guarda, e omesso significa
 * «tutti». Senza, il cruscotto di un redattore con tre obblighi assegnati
 * leggeva 118 nodi scoperti — quelli dell'intero ente — e il collegamento lo
 * portava a una pagina che gliene mostra al massimo tre. Due numeri sulla
 * stessa cosa, e nessun modo di capire quale contasse.
 */
export function nodiSenzaContenuti(
  nodi: readonly Nodo[],
  pubblicazioni: readonly Pubblicazione[],
  nodiAmmessi?: ReadonlySet<string>,
): Nodo[] {
  const nodiValorizzati = new Set(
    pubblicazioni
      .filter((pubblicazione) => pubblicazione.stato !== "archiviato")
      .map((pubblicazione) => pubblicazione.nodo_id)
      .filter((idNodo): idNodo is string => Boolean(idNodo)),
  );
  return nodiFoglia(nodi).filter(
    (nodo) =>
      nodo.applicabile &&
      !nodiValorizzati.has(nodo.id_nodo) &&
      (!nodiAmmessi || nodiAmmessi.has(nodo.id_nodo)),
  );
}

/**
 * Calcola i quattro indicatori del cruscotto.
 *
 * `regole` sono le cadenze dichiarate dal Comune: servono al KPI «In scadenza»
 * per contare con gli stessi termini dello Scadenzario, che le applica.
 */
export function calcolaIndicatori(
  nodi: readonly Nodo[],
  pubblicazioni: readonly Pubblicazione[],
  nodiAmmessi?: ReadonlySet<string>,
  regole: readonly RegolaObbligo[] = [],
): Indicatore[] {
  const attive = pubblicazioni.filter(
    (pubblicazione) => pubblicazione.stato === "pubblicato",
  ).length;
  const inScadenza = scadenzeImminenti(calcolaScadenze(nodi, pubblicazioni, regole)).length;
  const scoperti = nodiSenzaContenuti(nodi, pubblicazioni, nodiAmmessi).length;
  const fallite = documentiConValidazioneFallita(pubblicazioni).length;

  return [
    {
      chiave: "attive",
      etichetta: "Pubblicazioni attive",
      valore: attive,
      nota: "In stato «pubblicato», visibili sul portale.",
      tono: "neutro",
      collegamento: "/pubblicazioni?stato=pubblicato",
    },
    {
      chiave: "scadenza",
      etichetta: `In scadenza (${GIORNI_PREAVVISO} gg)`,
      valore: inScadenza,
      // Solo cio' che ha un termine. Gli obblighi mai valorizzati stanno nella
      // scheda accanto e non qui: contarli in entrambe faceva leggere come «in
      // scadenza» un insieme che non ha ancora nessuna scadenza di rinnovo, e
      // su un ente appena avviato portava questo numero a 121.
      nota: "Obblighi da aggiornare, documenti alla fine della pubblicazione e file da sostituire.",
      tono: inScadenza > 0 ? "attenzione" : "positivo",
      collegamento: "/scadenzario",
    },
    {
      chiave: "scoperti",
      etichetta: "Nodi senza contenuti",
      valore: scoperti,
      nota: "Applicabili e mai valorizzati: TrasparenzAI li rileva come vuoti.",
      tono: scoperti > 0 ? "attenzione" : "positivo",
      // Porta al filtro degli obblighi scoperti: su un ente in avviamento la
      // pagina li nasconde per difetto, ed e' proprio quando questo numero e'
      // grande. Senza il filtro, il collegamento portava a una tabella vuota.
      collegamento: "/scadenzario?tipo=obbligo_scoperto",
    },
    {
      chiave: "validazioni",
      etichetta: "Validazioni fallite",
      valore: fallite,
      nota: "Documenti bloccati dalla pipeline: non risultano pubblicati.",
      tono: fallite > 0 ? "critico" : "positivo",
      collegamento: "/pubblicazioni",
    },
  ];
}
